/**
 * TenantsPage — Task 25.2 (DEC-113). PLATFORM_ADMIN list of hauler tenants.
 *
 * Table (Name, Abbreviation, Drivers, Jobs 30d, Setup, Created, Status) with a
 * name search, an All/Active/Suspended filter, and a "New Tenant" action. Clicking
 * a row opens the tenant detail page.
 */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listFrom, sortRows, useUrlParam, useUrlSort } from '../../lib/url-state';
import { SortableHead } from 'src/components/ui/sortable-head';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { AddButton, StatusBadge, Pill, searchInputStyle, subText } from '../../ui/bits';
import { useTenants, type StatusFilter, type TenantSummary } from './hooks';
import { CreateTenantModal } from './CreateTenantModal';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** Local-by-context date (DEC-004 — never render UTC to users). */
function fmtDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function SetupCell({ t }: { t: TenantSummary }): React.JSX.Element {
  if (t.setupWizardAllComplete) return <Pill label="Complete" tone="positive" />;
  return (
    <span style={subText}>
      {t.setupWizardCompletionPct}% · step {t.setupWizardStep}/7
    </span>
  );
}

const FILTERS: readonly { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
];

/** The columns the Tenants table sorts by (OPEN-204); anything else in ?sort= is ignored. */
const TENANT_SORTS = ['name', 'abbreviation', 'drivers', 'jobs', 'setup', 'created'] as const;

export function TenantsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Corbin 2026-10-20: the status tab and search live in the URL (lib/url-state.ts).
  const [statusParam, setStatus] = useUrlParam('status', 'all');
  const status = statusParam as StatusFilter;
  const [search, setSearch] = useUrlParam('q');
  const [modalOpen, setModalOpen] = React.useState(false);

  const { data, isLoading, isError } = useTenants(status, 'name');

  // OPEN-204 — click a header to sort. The API still sends them by name (the default here), and
  // every tenant is loaded, so the sort runs in the browser.
  const { sort, toggle } = useUrlSort(TENANT_SORTS, { key: 'name', dir: 'asc' }, []);

  const matching = React.useMemo(() => {
    const all = data?.tenants ?? [];
    const q = search.trim().toLowerCase();
    if (q === '') return all;
    return all.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.haulerAbbreviation ?? '').toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q),
    );
  }, [data, search]);
  const tenants = sortRows(matching, sort, (t, k) => {
    switch (k) {
      case 'name':
        return t.name;
      case 'abbreviation':
        return t.haulerAbbreviation;
      case 'drivers':
        return t.driverCount;
      case 'jobs':
        return t.jobCount;
      case 'setup':
        return t.setupWizardAllComplete ? 100 : t.setupWizardCompletionPct;
      case 'created':
        return t.createdAt;
    }
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Hauler Tenants</h2>
          <p style={{ ...subText, marginTop: '2px' }}>
            {tenants.length} {tenants.length === 1 ? 'tenant' : 'tenants'}
          </p>
        </div>
        <AddButton label="New Tenant" onClick={(): void => setModalOpen(true)} />
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <input
          value={search}
          onChange={(e): void => setSearch(e.target.value)}
          placeholder="Search by name or abbreviation…"
          style={{ ...searchInputStyle, maxWidth: '320px' }}
          aria-label="Search tenants"
        />
        <div style={{ display: 'flex', gap: '4px' }}>
          {FILTERS.map((f) => (
            <Button
              key={f.id}
              type="button"
              variant={status === f.id ? 'default' : 'outline'}
              onClick={(): void => setStatus(f.id)}
              style={
                status === f.id
                  ? { backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }
                  : undefined
              }
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <p style={subText}>Loading tenants…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load tenants. Refresh to try again.
        </p>
      )}

      {!isLoading && !isError && (
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead column="name" sort={sort} onSort={toggle}>
                Name
              </SortableHead>
              <SortableHead column="abbreviation" sort={sort} onSort={toggle}>
                Abbrev
              </SortableHead>
              <SortableHead column="drivers" sort={sort} onSort={toggle}>
                Drivers
              </SortableHead>
              <SortableHead column="jobs" sort={sort} onSort={toggle}>
                Jobs (30d)
              </SortableHead>
              <SortableHead column="setup" sort={sort} onSort={toggle}>
                Setup
              </SortableHead>
              <SortableHead column="created" sort={sort} onSort={toggle}>
                Created
              </SortableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.map((t) => (
              <TableRow
                key={t.id}
                onClick={(): void =>
                  void navigate(`/tenants/${t.id}`, { state: listFrom(location) })
                }
                style={{ cursor: 'pointer' }}
              >
                <TableCell style={{ fontWeight: 500 }}>{t.name}</TableCell>
                <TableCell>{t.haulerAbbreviation ?? <span style={subText}>—</span>}</TableCell>
                <TableCell>{formatNumber(t.driverCount)}</TableCell>
                <TableCell>{formatNumber(t.jobCount)}</TableCell>
                <TableCell>
                  <SetupCell t={t} />
                </TableCell>
                <TableCell style={subText}>{fmtDate(t.createdAt)}</TableCell>
                <TableCell>
                  <StatusBadge active={t.isActive} />
                </TableCell>
              </TableRow>
            ))}
            {tenants.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} style={{ ...subText, textAlign: 'center', padding: '32px' }}>
                  No tenants match your filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <CreateTenantModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreated={(id): void => void navigate(`/tenants/${id}`)}
      />
    </div>
  );
}
